import React, { useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  SafeAreaView,
  Alert,
  ActivityIndicator,
  Modal,
  FlatList,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useNavigation } from '@react-navigation/native';
import * as DocumentPicker from 'expo-document-picker';
import { AppLayout } from '../../components/layout/AppLayout';
import { useAuthStore } from '../../stores/authStore';
import { useDriverStore } from '../../stores/driverStore';
import { uploadDriverPhoto } from '../../api/supabase';
import theme from '../../theme';

// CSV Row Interface
interface CSVDriverRow {
  Name: string;
  Code: string;
  Mobile: string;
  Address?: string;
  PHOTO?: string;
  'Captain\'s information'?: string;
  'RTA Expiry date'?: string;
  'ID Expiry Date'?: string;
  'Leave Status'?: string;
  'Leave Date'?: string;
  'Leave duration'?: string;
  CancellationStatus?: string;
  'Related Movements'?: string;
  'Number of movements per month'?: string;
  'Single & Partner'?: string;
  'Work&Stop&Vacation'?: string;
  'Last End Time'?: string;
  SMS?: string;
}

// Match Result Types
interface DriverMatch {
  type: 'update' | 'create' | 'delete';
  csvRow?: CSVDriverRow;
  existingDriver?: any;
  matchMethod?: 'code' | 'mobile' | 'name';
  changes?: string[];
  requiresConfirmation: boolean;
}

interface SyncSummary {
  toUpdate: DriverMatch[];
  toCreate: DriverMatch[];
  toDelete: DriverMatch[];
  statusChanges: DriverMatch[];
}

export const DriverSyncScreen = () => {
  const navigation = useNavigation();
  const { user } = useAuthStore();
  const { drivers, fetchDrivers, addDriver, updateDriver, removeDriver } = useDriverStore();
  
  const [selectedFile, setSelectedFile] = useState<any>(null);
  const [csvData, setCSVData] = useState<CSVDriverRow[]>([]);
  const [syncSummary, setSyncSummary] = useState<SyncSummary | null>(null);
  const [processing, setProcessing] = useState(false);
  const [currentStep, setCurrentStep] = useState<'upload' | 'review' | 'confirm' | 'processing' | 'complete'>('upload');
  const [confirmationModal, setConfirmationModal] = useState<{
    visible: boolean;
    type: 'update' | 'create' | 'delete' | 'status';
    items: DriverMatch[];
    currentIndex: number;
  }>({ visible: false, type: 'update', items: [], currentIndex: 0 });
  const [approvedActions, setApprovedActions] = useState<Set<number>>(new Set());
  const [processingResults, setProcessingResults] = useState<{
    updated: number;
    created: number;
    deleted: number;
    skipped: number;
    errors: string[];
  }>({ updated: 0, created: 0, deleted: 0, skipped: 0, errors: [] });
  const [syncProgress, setSyncProgress] = useState<{
    current: number;
    total: number;
    operation: string;
    driverName: string;
  }>({ current: 0, total: 0, operation: '', driverName: '' });

  // Check if user is superadmin
  const isSuperAdmin = user?.role === 'super_admin' || 
                       user?.email === 'omar@stsc.ae' || 
                       user?.email?.toLowerCase() === 'omar.m.fathy@gmail.com' ||
                       user?.email?.toLowerCase().includes('nizamudin');

  // Parse CSV file
  const parseCSV = (text: string): CSVDriverRow[] => {
    const lines = text.split('\n').filter(line => line.trim());
    if (lines.length < 2) {
      console.log('❌ CSV has less than 2 lines');
      return [];
    }

    // Detect separator: tab or comma (check first line)
    const separator = lines[0].includes('\t') ? '\t' : ',';
    console.log('📊 Detected separator:', separator === '\t' ? 'TAB' : 'COMMA');
    
    // Parse headers and remove quotes
    const headers = lines[0].split(separator).map(h => h.trim().replace(/^["']|["']$/g, ''));
    console.log('📋 Headers found:', headers.length, 'columns');
    console.log('📋 First 5 headers:', headers.slice(0, 5));
    
    const rows: CSVDriverRow[] = [];

    for (let i = 1; i < lines.length; i++) {
      // Parse values and remove quotes
      const values = lines[i].split(separator).map(v => v.trim().replace(/^["']|["']$/g, ''));
      const row: any = {};
      
      headers.forEach((header, index) => {
        row[header] = values[index] || '';
      });

      // Debug first row
      if (i === 1) {
        console.log('🔍 First data row:', {
          Name: row.Name,
          Code: row.Code,
          Mobile: row.Mobile,
          hasName: !!row.Name,
          hasCode: !!row.Code,
          hasMobile: !!row.Mobile
        });
      }

      // Only add rows with essential data
      if (row.Name && row.Name.trim() && (row.Code || row.Mobile)) {
        rows.push(row as CSVDriverRow);
      }
    }

    console.log('✅ Valid rows parsed:', rows.length);
    return rows;
  };

  // Pick and parse CSV file
  const pickFile = async () => {
    try {
      const result = await DocumentPicker.getDocumentAsync({
        type: 'text/csv',
        copyToCacheDirectory: true,
      });

      if (!result.canceled && result.assets[0]) {
        setSelectedFile(result.assets[0]);
        setProcessing(true);

        // Read file content
        const response = await fetch(result.assets[0].uri);
        const text = await response.text();
        const parsed = parseCSV(text);

        setCSVData(parsed);
        setProcessing(false);
        
        if (parsed.length > 0) {
          Alert.alert(
            'CSV Loaded',
            `Successfully loaded ${parsed.length} driver records. Ready to analyze.`,
            [{ text: 'OK' }]
          );
        } else {
          Alert.alert('Error', 'No valid driver records found in CSV file.');
        }
      }
    } catch (error) {
      setProcessing(false);
      Alert.alert('Error', 'Failed to read CSV file');
      console.error('CSV parsing error:', error);
    }
  };

  // Smart matching algorithm (Code → Mobile → Name)
  const matchDriver = (csvRow: CSVDriverRow): { driver: any; method: string } | null => {
    // Debug first row
    if (csvRow.Code === '70133') {
      console.log('🔍 Attempting to match Code:', csvRow.Code);
      console.log('🔍 Total drivers in DB:', drivers.length);
      const sample = drivers.slice(0, 3).map(d => ({
        id: d.id,
        driver_id: (d as any).driver_id,
        name: d.name,
        phone: d.phone
      }));
      console.log('🔍 First 3 drivers (full details):');
      sample.forEach((d, i) => {
        console.log(`  Driver ${i + 1}:`, JSON.stringify(d, null, 2));
      });
      
      // Check if ANY driver has driver_id
      const driversWithCode = drivers.filter(d => (d as any).driver_id).length;
      console.log(`📊 Drivers with driver_id: ${driversWithCode} out of ${drivers.length}`);
    }
    
    // 1. Try matching by Code (driver_id column)
    if (csvRow.Code && csvRow.Code.trim()) {
      const csvCode = csvRow.Code.trim();
      const match = drivers.find(d => (d as any).driver_id === csvCode);
      if (match) {
        console.log(`✅ Matched by Code: "${csvCode}" → Driver: ${match.name} (DB driver_id: ${(match as any).driver_id})`);
        return { driver: match, method: 'code' };
      } else {
        // Log when Code from CSV doesn't match any driver_id
        console.log(`⚠️  No match for Code: "${csvCode}" in driver_id column`);
      }
    }

    // 2. Fallback to Mobile
    if (csvRow.Mobile) {
      // Normalize: remove +, spaces, dashes, parentheses
      const cleanMobile = csvRow.Mobile.replace(/[\s\-\(\)\+]/g, '');
      const match = drivers.find(d => {
        const driverMobile = d.phone?.replace(/[\s\-\(\)\+]/g, '') || '';
        return driverMobile === cleanMobile;
      });
      if (match) {
        console.log(`✅ Matched by Mobile: "${csvRow.Mobile}" → Driver: ${match.name} (DB phone: ${match.phone})`);
        return { driver: match, method: 'mobile' };
      }
    }

    // 3. Last resort: Match by Name
    if (csvRow.Name) {
      const match = drivers.find(d => 
        d.name.toLowerCase().trim() === csvRow.Name.toLowerCase().trim()
      );
      if (match) return { driver: match, method: 'name' };
    }

    return null;
  };

  // Analyze CSV and create sync summary
  const analyzeCSV = async () => {
    if (csvData.length === 0) {
      Alert.alert('Error', 'Please upload a CSV file first');
      return;
    }

    setProcessing(true);
    
    // Ensure we have latest data
    console.log('🔄 Fetching drivers before analysis...');
    await fetchDrivers();
    
    // Wait a bit for store to update
    await new Promise(resolve => setTimeout(resolve, 500));
    
    console.log('📊 Starting analysis with', drivers.length, 'drivers in memory');
    
    if (drivers.length === 0) {
      setProcessing(false);
      Alert.alert('Error', 'No drivers found in database. Please ensure drivers are loaded.');
      return;
    }

    const toUpdate: DriverMatch[] = [];
    const toCreate: DriverMatch[] = [];
    const statusChanges: DriverMatch[] = [];
    const matchedDriverIds = new Set<string>();

    // Process each CSV row
    for (const csvRow of csvData) {
      const matchResult = matchDriver(csvRow);

      if (matchResult) {
        // Driver exists - check for updates
        const { driver, method } = matchResult;
        matchedDriverIds.add(driver.id);

        const changes: string[] = [];

        // Check for field changes
        if (csvRow.Name !== driver.name) {
          changes.push(`Name: "${driver.name}" → "${csvRow.Name}"`);
        }
        // Normalize phone numbers for comparison (ignore + prefix)
        const csvMobileNormalized = csvRow.Mobile?.replace(/[\s\-\(\)\+]/g, '') || '';
        const driverPhoneNormalized = driver.phone?.replace(/[\s\-\(\)\+]/g, '') || '';
        if (csvRow.Mobile && csvMobileNormalized !== driverPhoneNormalized) {
          changes.push(`Mobile: "${driver.phone}" → "${csvRow.Mobile}"`);
        }
        // Only flag Code as change if it differs from BOTH driver_id and careem_captain_id
        const csvCode = csvRow.Code?.trim();
        const hasDriverId = (driver as any).driver_id === csvCode;
        const hasCareemId = driver.careem_captain_id === csvCode;
        if (csvCode && !hasDriverId && !hasCareemId) {
          changes.push(`Code: "${driver.careem_captain_id || (driver as any).driver_id || 'none'}" → "${csvCode}"`);
        }
        // Only flag photo update if driver has no existing photo
        if (csvRow.PHOTO && !driver.image_url) {
          changes.push(`Photo: Added`);
        }

        // Check status changes (only for cancellation and vacation)
        const csvStatus = csvRow['Work&Stop&Vacation']?.toLowerCase();
        const shouldCheckStatus = csvStatus === 'cancellation' || csvStatus === 'vacation';
        
        if (shouldCheckStatus) {
          const newStatus = csvStatus === 'cancellation' ? 'inactive' : 
                           csvStatus === 'vacation' ? 'inactive' : driver.status;
          
          if (newStatus !== driver.status) {
            changes.push(`Status: "${driver.status}" → "${newStatus}" (${csvStatus})`);
            statusChanges.push({
              type: 'update',
              csvRow,
              existingDriver: driver,
              matchMethod: method as any,
              changes: [`Status change due to: ${csvStatus}`],
              requiresConfirmation: true,
            });
          }
        }

        if (changes.length > 0) {
          toUpdate.push({
            type: 'update',
            csvRow,
            existingDriver: driver,
            matchMethod: method as any,
            changes,
            requiresConfirmation: true,
          });
        }
      } else {
        // New driver - needs to be created
        toCreate.push({
          type: 'create',
          csvRow,
          requiresConfirmation: true,
        });
      }
    }

    // Find drivers in DB but not in CSV (potential deletes)
    const toDelete: DriverMatch[] = drivers
      .filter(driver => !matchedDriverIds.has(driver.id))
      .map(driver => ({
        type: 'delete' as const,
        existingDriver: driver,
        requiresConfirmation: true,
      }));

    setSyncSummary({ toUpdate, toCreate, toDelete, statusChanges });
    setCurrentStep('review');
    setProcessing(false);
  };

  //Show confirmation modal for specific action type
  const showConfirmations = (type: 'update' | 'create' | 'delete' | 'status') => {
    if (!syncSummary) return;

    const items = type === 'update' ? syncSummary.toUpdate :
                  type === 'create' ? syncSummary.toCreate :
                  type === 'delete' ? syncSummary.toDelete :
                  syncSummary.statusChanges;

    if (items.length === 0) {
      Alert.alert('Info', `No drivers to ${type}`);
      return;
    }

    // Calculate starting index offset based on type
    const startIndex = type === 'update' ? 0 :
                      type === 'create' ? syncSummary.toUpdate.length :
                      syncSummary.toUpdate.length + syncSummary.toCreate.length;

    setConfirmationModal({
      visible: true,
      type,
      items,
      currentIndex: startIndex, // Use global index
    });
  };

  // Handle confirmation decision
  const handleConfirmation = (approve: boolean) => {
    const { items, currentIndex, type } = confirmationModal;
    const currentItem = items[currentIndex];

    if (approve) {
      // Mark as approved using global index
      setApprovedActions(prev => new Set([...prev, currentIndex]));
    }

    // Calculate next index
    const startIndex = type === 'update' ? 0 :
                      type === 'create' ? syncSummary!.toUpdate.length :
                      syncSummary!.toUpdate.length + syncSummary!.toCreate.length;
    const localIndex = currentIndex - startIndex;

    // Move to next item or close modal
    if (localIndex < items.length - 1) {
      setConfirmationModal(prev => ({ ...prev, currentIndex: currentIndex + 1 }));
    } else {
      setConfirmationModal(prev => ({ ...prev, visible: false }));
      Alert.alert(
        'Review Complete',
        `Approved ${approvedActions.size + (approve ? 1 : 0)} out of ${items.length} ${type} operations.`,
        [{ text: 'OK' }]
      );
    }
  };

  // Execute approved actions
  const executeSync = async () => {
    if (!syncSummary) return;

    setProcessing(true);
    setCurrentStep('processing');
    const results = { updated: 0, created: 0, deleted: 0, skipped: 0, errors: [] as string[] };
    
    // Calculate total operations
    const totalOps = syncSummary.toUpdate.length + syncSummary.toCreate.length + syncSummary.toDelete.length;

    try {
      // Process updates
      for (let i = 0; i < syncSummary.toUpdate.length; i++) {
        if (!approvedActions.has(i)) {
          results.skipped++;
          continue;
        }

        const match = syncSummary.toUpdate[i];
        const { csvRow, existingDriver } = match;

        // Update progress
        setSyncProgress({
          current: i + 1,
          total: totalOps,
          operation: 'Updating',
          driverName: csvRow?.Name || existingDriver?.name || 'Unknown',
        });

        try {
          // Download photo ONLY if driver has no existing photo
          let photoUrl = existingDriver?.image_url;
          if (csvRow?.PHOTO && !photoUrl) {
            console.log(`📸 Downloading photo for ${csvRow?.Name} (no existing photo)`);
            photoUrl = await downloadAndUploadPhoto(csvRow.PHOTO, csvRow.Code);
          } else if (photoUrl) {
            console.log(`⏭️  Skipping photo for ${csvRow?.Name} (already has photo)`);
          }

          // Determine status
          const csvStatus = csvRow?.['Work&Stop&Vacation']?.toLowerCase();
          let status = existingDriver?.status;
          if (csvStatus === 'cancellation' || csvStatus === 'vacation') {
            status = 'inactive';
          }

          // Check if Code conflicts with another driver
          const csvCode = csvRow?.Code?.trim();
          const codeConflict = csvCode && drivers.find(d => 
            d.id !== existingDriver.id && 
            ((d as any).driver_id === csvCode || d.careem_captain_id === csvCode)
          );
          
          if (codeConflict) {
            console.warn(`⚠️  Skipping update for ${csvRow?.Name}: Code "${csvCode}" already used by ${codeConflict.name}`);
            results.errors.push(`Skipped ${csvRow?.Name}: Code "${csvCode}" already assigned to ${codeConflict.name}`);
            continue;
          }

          // Determine driver_id: Use CSV Code, or keep existing, or generate fallback
          // Database requires driver_id to be NOT NULL
          const existingDriverId = (existingDriver as any).driver_id;
          const newDriverId = csvCode || existingDriverId || `DRV-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

          await updateDriver(existingDriver.id, {
            name: csvRow?.Name || existingDriver.name,
            phone: csvRow?.Mobile || existingDriver.phone,
            driver_id: newDriverId,
            careem_captain_id: csvCode || existingDriver.careem_captain_id,
            image_url: photoUrl,
            status,
          });

          results.updated++;
        } catch (error) {
          results.errors.push(`Failed to update ${csvRow?.Name}: ${error}`);
        }
      }

      // Process creates
      const createStartIndex = syncSummary.toUpdate.length;
      for (let i = 0; i < syncSummary.toCreate.length; i++) {
        if (!approvedActions.has(createStartIndex + i)) {
          results.skipped++;
          continue;
        }

        const match = syncSummary.toCreate[i];
        const { csvRow } = match;

        // Update progress
        setSyncProgress({
          current: createStartIndex + i + 1,
          total: totalOps,
          operation: 'Creating',
          driverName: csvRow?.Name || 'Unknown',
        });

        try {
          // Check if Code conflicts with existing driver
          const csvCode = csvRow?.Code?.trim();
          const codeConflict = csvCode && drivers.find(d => 
            (d as any).driver_id === csvCode || d.careem_captain_id === csvCode
          );
          
          if (codeConflict) {
            console.warn(`⚠️  Skipping creation for ${csvRow?.Name}: Code "${csvCode}" already used by ${codeConflict.name}`);
            results.errors.push(`Skipped ${csvRow?.Name}: Code "${csvCode}" already assigned to ${codeConflict.name}`);
            results.skipped++;
            continue;
          }

          // Generate driver_id: Use CSV Code if available, otherwise create unique fallback
          // Database requires driver_id to be NOT NULL and UNIQUE
          const driverId = csvCode || `DRV-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

          // Download photo for new driver
          let photoUrl = '';
          if (csvRow?.PHOTO) {
            console.log(`📸 Downloading photo for new driver ${csvRow?.Name}`);
            photoUrl = await downloadAndUploadPhoto(csvRow.PHOTO, csvCode);
          }

          // Determine status
          const csvStatus = csvRow?.['Work&Stop&Vacation']?.toLowerCase();
          const status = (csvStatus === 'cancellation' || csvStatus === 'vacation') ? 'inactive' : 'active';

          // Normalize CSV fields: trim and treat empty strings as missing
          const csvLicenseNumberRaw = (csvRow?.Code ?? '').toString().trim();
          const csvLicenseNumber = csvLicenseNumberRaw === '' ? null : csvLicenseNumberRaw;

          const csvRtaExpiryRaw = (csvRow?.['RTA Expiry date'] ?? '').toString().trim();
          // Try to parse date in various common formats; if invalid or empty, fallback to 1 year from now
          let parsedExpiry: string | null = null;
          if (csvRtaExpiryRaw) {
            // Commonly CSV uses MM/DD/YYYY or M/D/YYYY; normalize using Date
            const maybeDate = new Date(csvRtaExpiryRaw);
            if (!isNaN(maybeDate.getTime())) {
              parsedExpiry = maybeDate.toISOString().split('T')[0];
            } else {
              // Try replacing all '/' with '-' and attempt again
              const replaced = csvRtaExpiryRaw.replace(/\//g, '-');
              const maybeDate2 = new Date(replaced);
              if (!isNaN(maybeDate2.getTime())) parsedExpiry = maybeDate2.toISOString().split('T')[0];
            }
          }

          const fallbackLicenseNumber = `LIC-${Date.now()}`;
          const fallbackExpiry = new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];

          await addDriver({
            name: (csvRow?.Name ?? '').toString().trim(),
            phone: (csvRow?.Mobile ?? '').toString().trim(),
            driver_id: driverId,
            careem_captain_id: csvCode || '',
            image_url: photoUrl,
            status,
            // Ensure DB receives either a non-empty license number or a generated fallback
            licenseNumber: csvLicenseNumber || fallbackLicenseNumber,
            // Ensure DB receives a valid YYYY-MM-DD expiry
            licenseExpiry: parsedExpiry || fallbackExpiry,
          });

          results.created++;
        } catch (error) {
          results.errors.push(`Failed to create ${csvRow?.Name}: ${error}`);
        }
      }

      // Process deletes
      const deleteStartIndex = syncSummary.toUpdate.length + syncSummary.toCreate.length;
      for (let i = 0; i < syncSummary.toDelete.length; i++) {
        if (!approvedActions.has(deleteStartIndex + i)) {
          results.skipped++;
          continue;
        }

        const match = syncSummary.toDelete[i];
        const { existingDriver } = match;

        // Update progress
        setSyncProgress({
          current: deleteStartIndex + i + 1,
          total: totalOps,
          operation: 'Deleting',
          driverName: existingDriver?.name || 'Unknown',
        });

        try {
          await removeDriver(existingDriver.id);
          results.deleted++;
        } catch (error) {
          results.errors.push(`Failed to delete ${existingDriver.name}: ${error}`);
        }
      }

      setProcessingResults(results);
      setCurrentStep('complete');
    } catch (error) {
      Alert.alert('Error', `Sync failed: ${error}`);
    } finally {
      setProcessing(false);
    }
  };

  // Download photo from URL and upload to Supabase Storage
  const downloadAndUploadPhoto = async (photoUrl: string, driverCode: string): Promise<string> => {
    try {
      if (!photoUrl || photoUrl === '') return '';

      // If already a Supabase URL, return as-is
      if (photoUrl.includes('supabase.co')) {
        return photoUrl;
      }

      // Download image
      const response = await fetch(photoUrl);
      const blob = await response.blob();

      // Upload to Supabase
      const filename = `driver-${driverCode}-${Date.now()}.jpg`;
      const result = await uploadDriverPhoto(blob, filename);

      return result.publicUrl || '';
    } catch (error) {
      console.error('Photo upload error:', error);
      return '';
    }
  };

  // Reset state
  const reset = () => {
    setSelectedFile(null);
    setCSVData([]);
    setSyncSummary(null);
    setCurrentStep('upload');
    setApprovedActions(new Set());
    setProcessingResults({ updated: 0, created: 0, deleted: 0, skipped: 0, errors: [] });
  };

  // Render confirmation modal content
  const renderConfirmationContent = () => {
    const { items, currentIndex, type } = confirmationModal;
    if (items.length === 0) return null;

    // Calculate local index within the current type
    const startIndex = type === 'update' ? 0 :
                      type === 'create' ? syncSummary!.toUpdate.length :
                      syncSummary!.toUpdate.length + syncSummary!.toCreate.length;
    const localIndex = currentIndex - startIndex;
    const currentItem = items[localIndex];

    if (type === 'update' && currentItem.existingDriver) {
      return (
        <View style={styles.confirmContent}>
          <Text style={styles.confirmTitle}>Update Driver?</Text>
          <Text style={styles.confirmSubtitle}>
            Matched by: {currentItem.matchMethod?.toUpperCase()}
          </Text>
          
          <View style={styles.driverCard}>
            <Text style={styles.driverName}>{currentItem.csvRow?.Name}</Text>
            <Text style={styles.driverDetail}>Code: {currentItem.csvRow?.Code}</Text>
            <Text style={styles.driverDetail}>Mobile: {currentItem.csvRow?.Mobile}</Text>
          </View>

          <Text style={styles.changesTitle}>Changes:</Text>
          {currentItem.changes?.map((change, idx) => (
            <Text key={idx} style={styles.changeItem}>• {change}</Text>
          ))}

          <Text style={styles.progressText}>
            {localIndex + 1} of {items.length}
          </Text>
        </View>
      );
    }

    if (type === 'create') {
      return (
        <View style={styles.confirmContent}>
          <Text style={styles.confirmTitle}>Add New Driver?</Text>
          
          <View style={styles.driverCard}>
            <Text style={styles.driverName}>{currentItem.csvRow?.Name}</Text>
            <Text style={styles.driverDetail}>Code: {currentItem.csvRow?.Code}</Text>
            <Text style={styles.driverDetail}>Mobile: {currentItem.csvRow?.Mobile}</Text>
            {currentItem.csvRow?.PHOTO && (
              <Text style={styles.driverDetail}>Photo: Will be downloaded</Text>
            )}
          </View>

          <Text style={styles.progressText}>
            {localIndex + 1} of {items.length}
          </Text>
        </View>
      );
    }

    if (type === 'delete' && currentItem.existingDriver) {
      return (
        <View style={styles.confirmContent}>
          <Text style={styles.confirmTitle}>Delete Driver?</Text>
          <Text style={styles.confirmSubtitle}>
            Not found in CSV file
          </Text>
          
          <View style={[styles.driverCard, { borderColor: theme.colors.error }]}>
            <Text style={styles.driverName}>{currentItem.existingDriver.name}</Text>
            <Text style={styles.driverDetail}>Code: {currentItem.existingDriver.careem_captain_id}</Text>
            <Text style={styles.driverDetail}>Mobile: {currentItem.existingDriver.phone}</Text>
          </View>

          <Text style={styles.progressText}>
            {localIndex + 1} of {items.length}
          </Text>
        </View>
      );
    }

    return null;
  };

  if (!isSuperAdmin) {
    return (
      <AppLayout title="Driver Sync" showBackButton onBackPress={() => navigation.goBack()}>
        <View style={styles.restrictedContainer}>
          <Ionicons name="lock-closed" size={64} color={theme.colors.error} />
          <Text style={styles.restrictedTitle}>Access Denied</Text>
          <Text style={styles.restrictedText}>
            This feature is only available to Super Administrators.
          </Text>
        </View>
      </AppLayout>
    );
  }

  return (
    <AppLayout title="Driver Sync" showBackButton onBackPress={() => navigation.goBack()}>
      <SafeAreaView style={styles.container}>
        <ScrollView style={styles.scrollView} showsVerticalScrollIndicator={false}>
          {/* Header */}
          <View style={styles.header}>
            <View style={[styles.iconContainer, { backgroundColor: '#EF4444' + '15' }]}>
              <Ionicons name="sync" size={24} color="#EF4444" />
            </View>
            <View style={styles.headerInfo}>
              <Text style={styles.title}>Driver Database Sync</Text>
              <Text style={styles.subtitle}>Upload CSV to sync driver information</Text>
            </View>
          </View>

          {/* Step 1: Upload */}
          {currentStep === 'upload' && (
            <>
              <View style={styles.section}>
                <Text style={styles.sectionTitle}>Step 1: Upload CSV File</Text>
                <TouchableOpacity 
                  style={[styles.filePickerButton, { borderColor: '#EF4444' }]} 
                  onPress={pickFile}
                  disabled={processing}
                >
                  <Ionicons name="cloud-upload" size={20} color="#EF4444" />
                  <Text style={[styles.filePickerText, { color: '#EF4444' }]}>
                    {selectedFile ? selectedFile.name : 'Choose Driver CSV file'}
                  </Text>
                </TouchableOpacity>
                
                {processing && (
                  <View style={styles.fileInfo}>
                    <ActivityIndicator size="small" color="#EF4444" />
                    <Text style={styles.fileInfoText}>
                      Loading CSV file...
                    </Text>
                  </View>
                )}
                
                {!processing && selectedFile && csvData.length > 0 && (
                  <View style={styles.fileInfo}>
                    <Ionicons name="checkmark-circle" size={16} color={theme.colors.success} />
                    <Text style={styles.fileInfoText}>
                      ✓ Loaded {csvData.length} driver records
                    </Text>
                  </View>
                )}

                {!processing && selectedFile && csvData.length === 0 && (
                  <View style={styles.fileInfo}>
                    <Ionicons name="alert-circle" size={16} color={theme.colors.error} />
                    <Text style={[styles.fileInfoText, { color: theme.colors.error }]}>
                      No valid records found. Check CSV format.
                    </Text>
                  </View>
                )}
              </View>

              {!processing && csvData.length > 0 && (
                <View style={styles.actionContainer}>
                  <TouchableOpacity 
                    style={[styles.primaryButton, { backgroundColor: '#EF4444' }]} 
                    onPress={analyzeCSV}
                    disabled={processing}
                  >
                    <Ionicons name="analytics" size={20} color={theme.colors.white} />
                    <Text style={styles.primaryButtonText}>Analyze Differences</Text>
                  </TouchableOpacity>
                </View>
              )}
            </>
          )}

          {/* Step 2: Review Summary */}
          {currentStep === 'review' && syncSummary && (
            <>
              <View style={styles.section}>
                <Text style={styles.sectionTitle}>Step 2: Review Changes</Text>
                
                <View style={styles.statsGrid}>
                  <View style={styles.statCard}>
                    <Text style={[styles.statNumber, { color: '#F59E0B' }]}>
                      {syncSummary.toUpdate.length}
                    </Text>
                    <Text style={styles.statLabel}>To Update</Text>
                    <TouchableOpacity 
                      style={styles.reviewButton}
                      onPress={() => showConfirmations('update')}
                    >
                      <Text style={styles.reviewButtonText}>Review</Text>
                    </TouchableOpacity>
                  </View>

                  <View style={styles.statCard}>
                    <Text style={[styles.statNumber, { color: theme.colors.success }]}>
                      {syncSummary.toCreate.length}
                    </Text>
                    <Text style={styles.statLabel}>To Create</Text>
                    <TouchableOpacity 
                      style={styles.reviewButton}
                      onPress={() => showConfirmations('create')}
                    >
                      <Text style={styles.reviewButtonText}>Review</Text>
                    </TouchableOpacity>
                  </View>

                  <View style={styles.statCard}>
                    <Text style={[styles.statNumber, { color: theme.colors.error }]}>
                      {syncSummary.toDelete.length}
                    </Text>
                    <Text style={styles.statLabel}>To Delete</Text>
                    <TouchableOpacity 
                      style={styles.reviewButton}
                      onPress={() => showConfirmations('delete')}
                    >
                      <Text style={styles.reviewButtonText}>Review</Text>
                    </TouchableOpacity>
                  </View>

                  <View style={styles.statCard}>
                    <Text style={[styles.statNumber, { color: '#8B5CF6' }]}>
                      {syncSummary.statusChanges.length}
                    </Text>
                    <Text style={styles.statLabel}>Status Changes</Text>
                    <TouchableOpacity 
                      style={styles.reviewButton}
                      onPress={() => showConfirmations('status')}
                    >
                      <Text style={styles.reviewButtonText}>Review</Text>
                    </TouchableOpacity>
                  </View>
                </View>
              </View>

              <View style={styles.actionContainer}>
                <TouchableOpacity 
                  style={[styles.primaryButton, { backgroundColor: '#EF4444' }]} 
                  onPress={executeSync}
                  disabled={approvedActions.size === 0}
                >
                  <Ionicons name="checkmark-done" size={20} color={theme.colors.white} />
                  <Text style={styles.primaryButtonText}>
                    Execute Sync ({approvedActions.size} approved)
                  </Text>
                </TouchableOpacity>

                <TouchableOpacity 
                  style={styles.secondaryButton} 
                  onPress={reset}
                >
                  <Ionicons name="close" size={20} color={theme.colors.textSecondary} />
                  <Text style={styles.secondaryButtonText}>Cancel</Text>
                </TouchableOpacity>
              </View>
            </>
          )}

          {/* Step 3: Processing */}
          {currentStep === 'processing' && (
            <View style={styles.section}>
              <ActivityIndicator size="large" color="#EF4444" />
              <Text style={styles.processingText}>Syncing drivers...</Text>
              
              {syncProgress.total > 0 && (
                <View style={styles.progressContainer}>
                  <Text style={styles.progressOperation}>
                    {syncProgress.operation} driver: {syncProgress.driverName}
                  </Text>
                  <Text style={styles.progressCounter}>
                    {syncProgress.current} / {syncProgress.total}
                  </Text>
                  <View style={styles.progressBar}>
                    <View 
                      style={[
                        styles.progressBarFill, 
                        { width: `${(syncProgress.current / syncProgress.total) * 100}%` }
                      ]} 
                    />
                  </View>
                </View>
              )}
            </View>
          )}

          {/* Step 4: Complete */}
          {currentStep === 'complete' && (
            <>
              <View style={styles.section}>
                <Text style={styles.sectionTitle}>Sync Complete!</Text>
                
                <View style={styles.resultsCard}>
                  <View style={styles.resultRow}>
                    <Text style={styles.resultLabel}>Updated:</Text>
                    <Text style={[styles.resultValue, { color: '#F59E0B' }]}>
                      {processingResults.updated}
                    </Text>
                  </View>
                  <View style={styles.resultRow}>
                    <Text style={styles.resultLabel}>Created:</Text>
                    <Text style={[styles.resultValue, { color: theme.colors.success }]}>
                      {processingResults.created}
                    </Text>
                  </View>
                  <View style={styles.resultRow}>
                    <Text style={styles.resultLabel}>Deleted:</Text>
                    <Text style={[styles.resultValue, { color: theme.colors.error }]}>
                      {processingResults.deleted}
                    </Text>
                  </View>
                  <View style={styles.resultRow}>
                    <Text style={styles.resultLabel}>Skipped:</Text>
                    <Text style={styles.resultValue}>
                      {processingResults.skipped}
                    </Text>
                  </View>
                </View>

                {processingResults.errors.length > 0 && (
                  <View style={styles.errorsCard}>
                    <Text style={styles.errorsTitle}>Errors ({processingResults.errors.length}):</Text>
                    {processingResults.errors.map((error, idx) => (
                      <Text key={idx} style={styles.errorText}>• {error}</Text>
                    ))}
                  </View>
                )}
              </View>

              <View style={styles.actionContainer}>
                <TouchableOpacity 
                  style={[styles.primaryButton, { backgroundColor: theme.colors.success }]} 
                  onPress={reset}
                >
                  <Ionicons name="refresh" size={20} color={theme.colors.white} />
                  <Text style={styles.primaryButtonText}>Sync Another File</Text>
                </TouchableOpacity>
              </View>
            </>
          )}
        </ScrollView>

        {/* Confirmation Modal */}
        <Modal
          visible={confirmationModal.visible}
          transparent
          animationType="slide"
          onRequestClose={() => setConfirmationModal(prev => ({ ...prev, visible: false }))}
        >
          <View style={styles.modalOverlay}>
            <View style={styles.modalContent}>
              {renderConfirmationContent()}

              <View style={styles.modalActions}>
                <TouchableOpacity 
                  style={[styles.modalButton, { backgroundColor: theme.colors.error }]}
                  onPress={() => handleConfirmation(false)}
                >
                  <Text style={styles.modalButtonText}>Reject</Text>
                </TouchableOpacity>

                <TouchableOpacity 
                  style={[styles.modalButton, { backgroundColor: theme.colors.success }]}
                  onPress={() => handleConfirmation(true)}
                >
                  <Text style={styles.modalButtonText}>Approve</Text>
                </TouchableOpacity>
              </View>

              <TouchableOpacity 
                style={styles.skipButton}
                onPress={() => setConfirmationModal(prev => ({ ...prev, visible: false }))}
              >
                <Text style={styles.skipButtonText}>Cancel Review</Text>
              </TouchableOpacity>
            </View>
          </View>
        </Modal>
      </SafeAreaView>
    </AppLayout>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: theme.colors.gray50,
  },
  scrollView: {
    flex: 1,
  },
  restrictedContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing.xl,
  },
  restrictedTitle: {
    fontSize: theme.typography.sizes['2xl'],
    fontWeight: '700' as any,
    color: theme.colors.error,
    marginTop: theme.spacing.lg,
    marginBottom: theme.spacing.sm,
  },
  restrictedText: {
    fontSize: theme.typography.sizes.md,
    color: theme.colors.textSecondary,
    textAlign: 'center',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: theme.spacing.lg,
    backgroundColor: theme.colors.white,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.gray200,
  },
  iconContainer: {
    width: 48,
    height: 48,
    borderRadius: theme.borderRadius.lg,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: theme.spacing.md,
  },
  headerInfo: {
    flex: 1,
  },
  title: {
    fontSize: theme.typography.sizes.xl,
    fontWeight: '700' as any,
    color: theme.colors.textPrimary,
    marginBottom: 4,
  },
  subtitle: {
    fontSize: theme.typography.sizes.md,
    color: theme.colors.textSecondary,
  },
  section: {
    backgroundColor: theme.colors.white,
    marginTop: theme.spacing.md,
    padding: theme.spacing.lg,
  },
  sectionTitle: {
    fontSize: theme.typography.sizes.lg,
    fontWeight: '600' as any,
    color: theme.colors.textPrimary,
    marginBottom: theme.spacing.md,
  },
  filePickerButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing.lg,
    borderWidth: 2,
    borderStyle: 'dashed',
    borderRadius: theme.borderRadius.lg,
    backgroundColor: '#EF4444' + '05',
    gap: theme.spacing.sm,
  },
  filePickerText: {
    fontSize: theme.typography.sizes.md,
    fontWeight: '500' as any,
  },
  fileInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: theme.spacing.sm,
    padding: theme.spacing.sm,
    backgroundColor: theme.colors.success + '10',
    borderRadius: theme.borderRadius.md,
    gap: theme.spacing.xs,
  },
  fileInfoText: {
    fontSize: theme.typography.sizes.sm,
    color: theme.colors.success,
  },
  statsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: theme.spacing.md,
  },
  statCard: {
    flex: 1,
    minWidth: '45%',
    alignItems: 'center',
    padding: theme.spacing.md,
    backgroundColor: theme.colors.gray50,
    borderRadius: theme.borderRadius.lg,
    gap: theme.spacing.xs,
  },
  statNumber: {
    fontSize: theme.typography.sizes['2xl'],
    fontWeight: '700' as any,
  },
  statLabel: {
    fontSize: theme.typography.sizes.sm,
    color: theme.colors.textSecondary,
    textAlign: 'center',
  },
  reviewButton: {
    marginTop: theme.spacing.xs,
    paddingVertical: theme.spacing.xs,
    paddingHorizontal: theme.spacing.sm,
    backgroundColor: theme.colors.white,
    borderRadius: theme.borderRadius.md,
    borderWidth: 1,
    borderColor: theme.colors.gray300,
  },
  reviewButtonText: {
    fontSize: theme.typography.sizes.sm,
    color: theme.colors.textPrimary,
    fontWeight: '500' as any,
  },
  actionContainer: {
    padding: theme.spacing.lg,
    gap: theme.spacing.md,
  },
  primaryButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: theme.spacing.md,
    paddingHorizontal: theme.spacing.lg,
    borderRadius: theme.borderRadius.lg,
    gap: theme.spacing.sm,
  },
  primaryButtonText: {
    fontSize: theme.typography.sizes.md,
    fontWeight: '600' as any,
    color: theme.colors.white,
  },
  secondaryButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: theme.colors.white,
    paddingVertical: theme.spacing.md,
    paddingHorizontal: theme.spacing.lg,
    borderRadius: theme.borderRadius.lg,
    borderWidth: 1,
    borderColor: theme.colors.gray300,
    gap: theme.spacing.sm,
  },
  secondaryButtonText: {
    fontSize: theme.typography.sizes.md,
    fontWeight: '600' as any,
    color: theme.colors.textSecondary,
  },
  processingText: {
    fontSize: theme.typography.sizes.lg,
    fontWeight: '500' as any,
    color: theme.colors.textPrimary,
    textAlign: 'center',
    marginTop: theme.spacing.md,
  },
  progressContainer: {
    marginTop: theme.spacing.lg,
    padding: theme.spacing.md,
    backgroundColor: theme.colors.gray50,
    borderRadius: theme.borderRadius.lg,
    width: '100%',
  },
  progressOperation: {
    fontSize: theme.typography.sizes.md,
    fontWeight: '500' as any,
    color: theme.colors.textPrimary,
    marginBottom: theme.spacing.xs,
  },
  progressCounter: {
    fontSize: theme.typography.sizes.sm,
    color: theme.colors.textSecondary,
    marginBottom: theme.spacing.sm,
  },
  progressBar: {
    height: 8,
    backgroundColor: theme.colors.gray200,
    borderRadius: 4,
    overflow: 'hidden',
  },
  progressBarFill: {
    height: '100%',
    backgroundColor: theme.colors.primary,
    borderRadius: 4,
  },
  resultsCard: {
    padding: theme.spacing.lg,
    backgroundColor: theme.colors.gray50,
    borderRadius: theme.borderRadius.lg,
    gap: theme.spacing.sm,
  },
  resultRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  resultLabel: {
    fontSize: theme.typography.sizes.md,
    color: theme.colors.textSecondary,
  },
  resultValue: {
    fontSize: theme.typography.sizes.lg,
    fontWeight: '600' as any,
    color: theme.colors.textPrimary,
  },
  errorsCard: {
    marginTop: theme.spacing.md,
    padding: theme.spacing.md,
    backgroundColor: theme.colors.error + '10',
    borderRadius: theme.borderRadius.lg,
    borderWidth: 1,
    borderColor: theme.colors.error + '20',
  },
  errorsTitle: {
    fontSize: theme.typography.sizes.md,
    fontWeight: '600' as any,
    color: theme.colors.error,
    marginBottom: theme.spacing.sm,
  },
  errorText: {
    fontSize: theme.typography.sizes.sm,
    color: theme.colors.error,
    marginBottom: theme.spacing.xs,
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: theme.spacing.lg,
  },
  modalContent: {
    width: '100%',
    maxWidth: 400,
    backgroundColor: theme.colors.white,
    borderRadius: theme.borderRadius.xl,
    padding: theme.spacing.xl,
  },
  confirmContent: {
    marginBottom: theme.spacing.lg,
  },
  confirmTitle: {
    fontSize: theme.typography.sizes.xl,
    fontWeight: '700' as any,
    color: theme.colors.textPrimary,
    marginBottom: theme.spacing.xs,
  },
  confirmSubtitle: {
    fontSize: theme.typography.sizes.sm,
    color: theme.colors.textSecondary,
    marginBottom: theme.spacing.md,
  },
  driverCard: {
    padding: theme.spacing.md,
    backgroundColor: theme.colors.gray50,
    borderRadius: theme.borderRadius.lg,
    borderWidth: 1,
    borderColor: theme.colors.gray200,
    marginBottom: theme.spacing.md,
  },
  driverName: {
    fontSize: theme.typography.sizes.lg,
    fontWeight: '600' as any,
    color: theme.colors.textPrimary,
    marginBottom: theme.spacing.xs,
  },
  driverDetail: {
    fontSize: theme.typography.sizes.sm,
    color: theme.colors.textSecondary,
    marginBottom: 2,
  },
  changesTitle: {
    fontSize: theme.typography.sizes.md,
    fontWeight: '600' as any,
    color: theme.colors.textPrimary,
    marginBottom: theme.spacing.xs,
  },
  changeItem: {
    fontSize: theme.typography.sizes.sm,
    color: theme.colors.textSecondary,
    marginBottom: theme.spacing.xs,
    lineHeight: 18,
  },
  progressText: {
    fontSize: theme.typography.sizes.sm,
    color: theme.colors.textSecondary,
    textAlign: 'center',
    marginTop: theme.spacing.sm,
  },
  modalActions: {
    flexDirection: 'row',
    gap: theme.spacing.md,
  },
  modalButton: {
    flex: 1,
    paddingVertical: theme.spacing.md,
    borderRadius: theme.borderRadius.lg,
    alignItems: 'center',
  },
  modalButtonText: {
    fontSize: theme.typography.sizes.md,
    fontWeight: '600' as any,
    color: theme.colors.white,
  },
  skipButton: {
    marginTop: theme.spacing.md,
    padding: theme.spacing.sm,
    alignItems: 'center',
  },
  skipButtonText: {
    fontSize: theme.typography.sizes.sm,
    color: theme.colors.textSecondary,
  },
});
